import React from 'react';

/**
 * Single row inside a Health panel subsection. Severity dot with a 3px
 * soft halo, two-line text stack, optional count chip, optional action
 * (e.g. "Review →").
 *
 *   severity: 'critical' | 'warning'
 *
 * The OUTER panel that hosts these rows uses a soft (-soft) background
 * — the severity color only appears on the dot, the optional severity
 * chip, and (optionally) the count chip. Per handoff: no screaming red
 * banners.
 */
const TONE = {
  critical: 'var(--danger)',
  warning: 'var(--warning)',
};

export default function AlertRow({ severity = 'warning', primary, secondary, count, action }) {
  const tone = TONE[severity] || TONE.warning;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 12px',
      }}
    >
      {/* Severity dot with halo */}
      <span
        aria-hidden
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 14,
          height: 14,
          flexShrink: 0,
        }}
      >
        <span style={{
          width: 8, height: 8, borderRadius: 999, background: tone,
          boxShadow: `0 0 0 3px color-mix(in oklch, ${tone} 18%, transparent)`,
        }} />
      </span>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <div
          style={{
            fontSize: 14.5,
            fontWeight: 500,
            color: 'var(--text-1)',
            letterSpacing: '-0.005em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={typeof primary === 'string' ? primary : undefined}
        >
          {primary}
        </div>
        {secondary && (
          <div style={{ fontSize: 12.5, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {secondary}
          </div>
        )}
      </div>
      {count != null && (
        <span
          className="mono"
          style={{
            fontSize: 12.5,
            fontWeight: 500,
            color: 'var(--text-2)',
            background: 'var(--bg-surface-2)',
            border: '1px solid var(--border-subtle)',
            padding: '2px 8px',
            borderRadius: 999,
            flexShrink: 0,
          }}
        >
          {count}
        </span>
      )}
      {severity === 'critical' && !count && (
        <span style={{
          fontSize: 12.5, fontWeight: 500,
          color: 'var(--danger)',
          background: 'var(--danger-soft)',
          border: '1px solid color-mix(in oklch, var(--danger) 22%, transparent)',
          padding: '2px 8px', borderRadius: 999,
        }}>critical</span>
      )}
      {severity === 'warning' && !count && (
        <span style={{
          fontSize: 12.5, fontWeight: 500,
          color: 'var(--warning)',
          background: 'var(--warning-soft)',
          border: '1px solid color-mix(in oklch, var(--warning) 22%, transparent)',
          padding: '2px 8px', borderRadius: 999,
        }}>warning</span>
      )}
      {action}
    </div>
  );
}
